// Split out of src/components/Tests.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Tests.jsx
// re-exports everything it exported before, so existing imports keep working.

import { DoubledTokens, Celebration, Icon } from "../Common.jsx";
import { useGameTelemetry } from "../../lib/metrics.js";
import { oinkSessionOP } from "../../lib/oinks.js";
import { shuffle } from "../../lib/constants.js";
import { audioKeyFor, prefetchClip, resetRetryBudget, speak as speakText, stopSpeaking, unlockAudio } from "../../lib/speech.js";
import { t as ct } from "../../lib/content.js";
import { deferToEnd, OinkIntroLine, TestOverlay, scoreEmoji, TokenRewardRow, OinkRewardRow } from "./shared.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

export function VocabTest({ doubleDay = false, bank, hasAnyWords, onLearnEarn, onWordAnswered, onItemResults, passReward, fullReward, passThreshold, testSize, payoutsPerWeek, payGate, payoutsUsed, kidTheme, weeklyCapMessage, oinksOn, onEarnOinks, partyEmoji, onSessionFinished }) {
  const [sessionKey, setSessionKey] = useState(0);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [phase, setPhase] = useState("intro");
  // W2.5 Learn & Earn funnel: game_start / game_complete / game_abandon ride the
  // shared phase shape (intro → testing → finished). Age band is stamped by the
  // metrics module (App.jsx keeps it current), so no child prop is needed here.
  useGameTelemetry("vocab", phase);
  const [awarded, setAwarded] = useState(false);
  // Set when onLearnEarn routes this pass's reward to the parent instead of crediting it
  // instantly (spending cap breached/blocked) — holds the kid-friendly message to show in
  // place of the usual reward celebration.
  const [capNotice, setCapNotice] = useState(null);
  // The words drawn for THIS attempt. Captured once in startTest and left alone for the rest
  // of the session — see the bug note below.
  const vocabResultsRef = useRef([]);
  // LB-366: the same answers as vocabResultsRef, with what the child picked and the right
  // meaning — display text for the test history, never given to the engine.
  const vocabHistoryRef = useRef([]);
  const vocabPassRef = useRef(passThreshold); vocabPassRef.current = passThreshold; // read on unmount, so a ref
  // Words parked once already — a second skip is final (see deferToEnd).
  const vocabDeferredRef = useRef(new Set());
  const [session, setSession] = useState([]);
  // W1.13 (Aug 2026) — closes the gap recorded at W2.3 delivery: vocab-word audio was
  // already being generated server-side ("vocab-word" in worker/lib/tts-content.js) but
  // this screen had no playback UI at all. Tap-to-hear only, no autoplay (Alan's call):
  // the child clicks the speaker to hear the word pronounced. Same tiered playback as the
  // Spelling Test — pre-generated clip when one exists, device voice otherwise.
  const [audioKey, setAudioKey] = useState(null);
  const [speechOk, setSpeechOk] = useState(true);

  const perWeek = payoutsPerWeek || 1;
  // 12 Sep (Alan): on Coach and By-Year lists a game pays once at the PASS tier and once at
  // FULL marks, so scoring 80% no longer ends the week — going back for 100% still pays,
  // which is the behaviour worth encouraging. Custom lists keep the plain weekly count,
  // because replaying a parent's own short word list IS farmable. payGate falls back to the
  // old rule when a caller has not supplied one.
  const gate = payGate || { full: (payoutsUsed || 0) < perWeek, pass: (payoutsUsed || 0) < perWeek };
  const onCooldown = !gate.full && !gate.pass;
  // The test settles — pays, logs to Coach, records stats — the moment the LAST answer is
  // picked, in settleVocab(), not when the results screen is opened. "See results" and the
  // ✕ both used to stand between a completed test and anything being saved, so a child who
  // answered everything and then closed the overlay lost the whole test: no payout, no
  // activity row, no Coach entry (Alan, 22 Sep). settledRef is the single guard for both
  // routes into settlement (the last answer, and the phase effect kept as a safety net).
  const settledRef = useRef(false);
  // A test abandoned mid-attempt still reaches Coach as a "stopped early" entry that pays
  // nothing and moves nothing — see reportStopped below and its twin in SpellingTest.
  const stoppedReportedRef = useRef(false);
  const phaseRef = useRef(phase); phaseRef.current = phase;
  const sessionLenRef = useRef(0);
  const onItemResultsRef = useRef(onItemResults); onItemResultsRef.current = onItemResults;
  // Snapshot of onCooldown taken the moment this test settles, then frozen. Without this,
  // the results screen has a real bug: crediting THIS session's reward updates payoutsUsed
  // via the parent's state, which re-renders this component with onCooldown now true —
  // since the very payout being reported is what just pushed the weekly count to its limit
  // — so the "you earned it" card flips to "you're already on cooldown" the instant the
  // reward that just earned it lands (staging feedback, Aug 2026). Never overwritten once
  // the test has settled: settlement happens while phase is still "testing", and this
  // session's own payout is exactly what flips onCooldown — without the settledRef guard
  // the next render would re-freeze the value to true and the results screen would deny
  // the reward it just paid.
  const onCooldownAtFinishRef = useRef(onCooldown);
  if (phase !== "finished" && !settledRef.current) onCooldownAtFinishRef.current = onCooldown;

  // Shuffled per question, so the correct definition isn't always sitting in the same (e.g.
  // first) position in the option list.
  // A word whose CMS entry is missing has no options — never let that reach shuffle() (the
  // "e is not iterable" crash Alan hit on staging, 7 Sep). Words without options are dropped
  // at draw time below; this guard covers any that slip through.
  const options = useMemo(() => (session[idx] && Array.isArray(session[idx].options) ? shuffle(session[idx].options) : []), [session, idx]);

  function settleVocab() {
    if (settledRef.current || session.length === 0) return;
    const rows = vocabResultsRef.current;
    if (rows.length < session.length) return; // a parked word is still to come back
    settledRef.current = true;
    setAwarded(true);
    onCooldownAtFinishRef.current = onCooldown; // frozen BEFORE this payout bumps the weekly count
    // Computed from the recorded rows, not from `correctCount` — settlement runs inside the
    // same event handler that recorded the final answer, one render before that state lands.
    const correct = rows.filter((r) => r.correct).length;
    const pct = (100 * correct) / session.length;
    // Oinks first: paid on every completed session regardless of cooldown, cap, or score.
    const sessionOinks = oinksOn && onEarnOinks ? oinkSessionOP(correct, pct >= 100).op : 0;
    if (oinksOn && onEarnOinks) onEarnOinks(sessionOinks);
    if (onSessionFinished) onSessionFinished("vocab", correct, session.length, pct >= 100);
    const reward = pct >= 100 ? fullReward : pct >= passThreshold ? passReward : 0;
    const tier = pct >= 100 ? "full" : "pass";
    const history = { questions: vocabHistoryRef.current.slice(), oinks: sessionOinks, qualified: reward > 0 ? tier : null, gateOpen: reward > 0 && !!gate[tier], passMark: passThreshold, passKind: "pct" };
    if (onItemResults) onItemResults("vocab", rows.slice(), false, { total: session.length, history });
    if (reward > 0 && gate[tier]) {
      const result = onLearnEarn(reward, `Vocabulary Test: ${correct}/${session.length} correct (${Math.round(pct)}%)`, "vocab", tier);
      if (result && !result.credited) setCapNotice(result.capMessage || null);
    }
  }
  useEffect(() => { if (phase === "finished") settleVocab(); }, [phase]);
  function reportStopped() {
    if (stoppedReportedRef.current || settledRef.current) return;
    if (phaseRef.current !== "testing") return;
    const rows = vocabResultsRef.current;
    if (!rows.length) return;
    stoppedReportedRef.current = true;
    const history = { questions: vocabHistoryRef.current.slice(), oinks: 0, qualified: null, gateOpen: false, passMark: vocabPassRef.current, passKind: "pct" };
    if (onItemResultsRef.current) onItemResultsRef.current("vocab", rows.slice(), true, { total: sessionLenRef.current, history });
  }

  // LB-282: these sit ABOVE the empty-bank early return below, deliberately. They used to sit
  // under it, which meant a component that returned early called two fewer hooks — so a word
  // bank that arrives late, or empties, changes the hook count between renders and React
  // throws. Found by react-hooks/rules-of-hooks. `session` is empty rather than absent in that
  // state, so both hooks are safe to run unconditionally.
  const currentItem = session[idx];
  const speechTextForItem = (currentItem && currentItem.speakAs && currentItem.speakAs.trim()) || (currentItem && currentItem.word) || "";
  useEffect(() => {
    let live = true;
    setAudioKey(null);
    if (!speechTextForItem) return () => { live = false; };
    audioKeyFor(speechTextForItem, "teacher").then((k) => {
      if (!live) return;
      setAudioKey(k);
      if (k) prefetchClip(k);
    });
    return () => { live = false; };
  }, [speechTextForItem]);
  useEffect(() => () => { stopSpeaking(); reportStopped(); }, []);

  const playable = (bank || []).filter((w) => w && Array.isArray(w.options) && w.options.length >= 2);
  if (!bank || bank.length === 0 || playable.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 text-center text-base text-slate-400 shadow-sm border border-slate-100">
        {bank && bank.length > 0 && playable.length === 0
          ? ct("vocab.meanings_not_loaded", "These words don't have their meanings loaded yet — try again in a moment, or ask a parent to check Learn & Earn.")
          : hasAnyWords
          ? ct("vocab.all_mastered", "You've mastered every word in rotation right now — nice work. Ask a parent to add more, or turn some back on.")
          : "No vocabulary words set up yet — ask a parent to add some."}
      </div>
    );
  }

  // BUG FIX (2026-08-15): this used to draw the session with useMemo(() => ..., [bank, ...]),
  // which recomputed — reshuffling onto a fresh set of words mid-test — every time `bank`'s
  // array reference changed. `bank` is a derived/filtered prop (see ChildView's LearnEarnTab),
  // so it gets a new reference on every answer (a word's correctCount changes, which changes
  // eligibility) and after any background re-render (e.g. returning to the tab). idx and
  // picked didn't reset when that happened, so the test appeared to silently jump to a
  // different, already-in-progress-looking question. Drawing the session into plain state
  // exactly once, here, means nothing outside startTest can change it mid-attempt.
  // Same clip-id wiring as SpellingTest, same reasons: hash as soon as the word changes so
  // the tap-to-play path stays synchronous, mirror the server's speakAs-over-word rule
  // (worker/lib/tts-content.js narrationTextFor — the D13 homophone override applies to
  // vocab words too), and guard against a stale in-flight hash landing after the child has
  // already moved on.

  function hearWord() {
    if (!currentItem) return;
    if (!speechOk) { setSpeechOk(true); resetRetryBudget(); } // manual retry tap
    speakText(speechTextForItem || currentItem.word, {
      audioKey,
      rate: 0.85,
      isAutoplay: false,
      onUnavailable: () => setSpeechOk(false),
    });
  }

  function startTest() {
    // The one guaranteed user gesture before any speaker tap — see SpellingTest.startTest.
    unlockAudio();
    vocabDeferredRef.current = new Set();
    vocabResultsRef.current = [];
    vocabHistoryRef.current = [];
    settledRef.current = false;
    stoppedReportedRef.current = false;
    const drawn = shuffle((bank || []).filter((w) => w && Array.isArray(w.options) && w.options.length >= 2)).slice(0, testSize);
    sessionLenRef.current = drawn.length;
    setSession(drawn);
    setSessionKey((k) => k + 1); setIdx(0); setPicked(null); setCorrectCount(0); setAwarded(false); setCapNotice(null); setPhase("testing");
  }
  function restart() { stopSpeaking(); reportStopped(); setPhase("intro"); }
  function choose(opt, skipped = false) {
    if (picked) return;
    // Same rule as spelling: a first skip parks the word at the end of the round rather
    // than scoring it (see deferToEnd).
    if (skipped && !vocabDeferredRef.current.has(session[idx].id)) {
      const reordered = deferToEnd(session, idx);
      if (reordered) {
        vocabDeferredRef.current.add(session[idx].id);
        setSession(reordered);
        setPicked(null);
        return;
      }
    }
    setPicked(skipped ? "__skipped__" : opt);
    const isCorrect = !skipped && opt === session[idx].definition;
    if (isCorrect) setCorrectCount((c) => c + 1);
    // Reported immediately (not batched to test-end) and independent of reward/cooldown —
    // this is word-mastery progress, not money, so it should count every time regardless of
    // whether this session ends up paying out. See App.jsx's onVocabWordResult: a word gets
    // taken out of rotation once it's been answered correctly twice in total.
    if (onWordAnswered) onWordAnswered(session[idx].id, isCorrect);
    vocabResultsRef.current.push({ id: session[idx].id, word: session[idx].word, grade: session[idx].grade, topic: session[idx].topic, correct: isCorrect });
    vocabHistoryRef.current.push({ q: session[idx].word, given: skipped ? "" : opt, answer: session[idx].definition, correct: isCorrect, skipped: !!skipped });
    // The last answer settles the test right here — before "See results", before anything
    // the child might do instead of tapping it.
    settleVocab();
  }
  function next() { stopSpeaking(); if (idx + 1 >= session.length) { setPhase("finished"); return; } setIdx((i) => i + 1); setPicked(null); }


  if (phase === "intro") {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
        <div className="mb-2 flex justify-center"><Icon id="ic-vocab" size={36} hue="indigo" /></div>
        <div className="font-semibold text-slate-700 text-sm mb-1">{Math.min(testSize, (bank || []).length)} word{Math.min(testSize, (bank || []).length) === 1 ? "" : "s"} this test</div>
        <p className="text-base text-slate-500 mb-4">
          {weeklyCapMessage
            ? weeklyCapMessage
            : onCooldown
            ? `Practice only — you've already earned ${perWeek > 1 ? `all ${perWeek} rewards` : "this week's reward"} for Vocab, resets Monday.`
            : doubleDay
            ? <>Score 100% to earn <DoubledTokens plus={false} base={fullReward} /> tokens, or {passThreshold}%+ for <DoubledTokens plus={false} base={passReward} />. <span className="text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 whitespace-nowrap">{ct("tests.double_token_chip", "Double token day")}</span></>
            : `Score 100% to earn ${fullReward} tokens, or ${passThreshold}%+ for ${passReward}.`}
        </p>
        {oinksOn && <OinkIntroLine moneyPaused={!!weeklyCapMessage || onCooldown} />}
        <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm w-full`} onClick={startTest}>{ct("tests.start_button", "Start test")}</button>
      </div>
    );
  }

  return (
    <TestOverlay onClose={restart} focused={phase === "testing"}>
      {phase === "finished" ? (() => {
        const pct = Math.round((correctCount / session.length) * 100);
        const reward = pct >= 100 ? fullReward : pct >= passThreshold ? passReward : 0;
        const rewardEarned = reward > 0 && !onCooldownAtFinishRef.current && !capNotice;
        const oink = oinkSessionOP(correctCount, pct >= 100);
        return (
          <div className="relative overflow-hidden bg-white rounded-2xl p-6 shadow-sm border border-slate-100 text-center">
            {/* Was gated on pct >= 100 — a pass-mark score genuinely earns tokens too (see the
                🎉 messaging right below, which already celebrates it), so withholding the
                confetti made "you earned tokens" and "you get a celebration" inconsistent. */}
            {rewardEarned && <Celebration variant="pig" emojiOverride={partyEmoji} />}
            <div className={`font-extrabold text-3xl mb-1 ${rewardEarned ? "text-emerald-500" : "text-slate-700"}`}>{scoreEmoji(pct)} {correctCount}/{session.length} <span className="text-lg text-slate-400">({pct}%)</span></div>
            {capNotice ? (
              <div className="text-amber-600 font-bold text-base mb-4">{capNotice}</div>
            ) : rewardEarned ? (
              <TokenRewardRow amount={reward} detail={`${correctCount}/${session.length} correct (${pct}%)${reward === fullReward ? " — full marks" : ""}`} />
            ) : reward > 0 ? (
              <div className="text-slate-500 text-base mb-4">{reward === fullReward ? "Full marks" : `${passThreshold}%+ correct`} You've already earned {perWeek > 1 ? "all this week's Vocab rewards" : "this week's Vocab reward"} — resets Monday.</div>
            ) : (
              <div className="text-slate-500 text-base mb-4">Score {passThreshold}% or higher to earn tokens.</div>
            )}
            {oinksOn && <OinkRewardRow op={oink.op} detail={oink.detail} />}
            <button className={`${kidTheme.primaryBtn} text-white font-bold rounded-xl px-5 py-2.5 text-sm`} onClick={restart}>{reward > 0 ? "Back to Learn & Earn" : "Try again"}</button>
          </div>
        );
      })() : (() => {
        const item = session[idx];
        return (
          <div key={`q-${sessionKey}-${idx}`} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs text-slate-400 font-medium text-left">Word {idx + 1} of {session.length} · {onCooldown ? "practice only — reward resets Monday" : `${passThreshold}%+ earns ${passReward}, 100% earns ${fullReward}`}</div>
              <button className="lb-inline-quit text-xs text-slate-400 font-semibold flex-shrink-0 ml-2" onClick={restart}>{ct("tests.quit_restart", "Quit / Restart")}</button>
            </div>
            {/* Stacked (Alan, 23 Sep): the word gets its own line and Listen sits under it. On
                one row a long word ("accommodation") pushed Listen out of the card. */}
            <div className="flex flex-col items-center gap-2.5 mb-4">
              <div className="font-extrabold text-2xl text-slate-800 text-center max-w-full" style={{ overflowWrap: "anywhere" }}>{item.word}</div>
              <button data-testid="vocab-hear-word" aria-label={ct("vocab.hear_word_label", "Hear the word")} onClick={hearWord}
                className="bg-amber-50 text-amber-700 rounded-lg h-10 px-3 flex items-center justify-center text-sm font-bold flex-shrink-0">{ct("tests.listen", "Listen")}</button>
            </div>
            {!speechOk && (
              <p className="text-xs font-semibold text-amber-600 text-center -mt-2 mb-3">{ct("vocab.audio_unavailable", "Audio isn't available right now — tap Listen to try again, or ask a parent to read it aloud.")}</p>
            )}
            <div className="flex flex-col gap-2">
              {options.map((opt) => {
                const isCorrect = opt === item.definition;
                const show = picked !== null;
                return (
                  <button key={opt} data-testid="vocab-option" onClick={() => choose(opt)}
                    className={`text-left text-base rounded-xl border px-3 py-2.5 ${show ? (isCorrect ? "bg-emerald-50 border-emerald-300 text-emerald-700" : opt === picked ? "bg-rose-50 border-rose-300 text-rose-700" : "border-slate-100 text-slate-400") : "border-slate-200 text-slate-600"}`}>
                    {opt}
                  </button>
                );
              })}
            </div>
            {!picked && <button className="w-full mt-3 text-sm font-bold text-slate-500 py-2" data-testid="test-skip" onClick={() => choose(null, true)}>{vocabDeferredRef.current.has(item.id) || idx >= session.length - 1 ? ct("tests.skip", "Skip this word") : ct("tests.skip_for_now", "Skip for now")}</button>}
            {picked && <button className="mt-4 text-base font-semibold text-slate-400 w-full text-center" onClick={next}>{idx + 1 >= session.length ? "See results →" : "Next word →"}</button>}
          </div>
        );
      })()}
    </TestOverlay>
  );
}
